import { useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { Observation, Practitioner } from "@medplum/fhirtypes";
import { Loading, useMedplum, useMedplumProfile } from "@medplum/react";

import { ActionIcon, Box, Button, Drawer, Flex, Group, Title, Tooltip } from "@mantine/core";
import { DateInput, TimeInput } from "@mantine/dates";
import { notifications } from "@mantine/notifications";

import * as Sentry from "@sentry/react";
import { IconCalendar, IconClock, IconX } from "@tabler/icons-react";
import { format } from "date-fns";

import {
  LABOR_AMEND_FLOWSHEET_SAVE_ERROR_MILESTONE_MESSAGE,
  LABOR_AMEND_FLOWSHEET_SAVE_ERROR_TITLE,
  LABOR_AMEND_QUEUED_MESSAGE,
} from "@/encounters/components/EncounterLabor/constants/labor-encounter-amend";
import { useEnqueueLaborAmendPersist } from "@/encounters/components/EncounterLabor/hooks/use-enqueue-labor-amend-persist";
import { getLaborMilestoneReplaceKey } from "@/encounters/components/EncounterLabor/utils/labor-amend-pending-keys";
import { getCodeableConceptDisplay } from "@/utils/medplum";

interface EditMilestoneRowProps {
  observation: Observation;
  onClose?: () => void;
}

export default function EditMilestoneRow({ observation, onClose }: EditMilestoneRowProps) {
  const [opened, setOpened] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const timeInputRef = useRef<HTMLInputElement>(null);
  const [dateTime, setDateTime] = useState(
    observation.effectiveDateTime ? new Date(observation.effectiveDateTime) : new Date(),
  );

  const { encounterId, clientId } = useParams();
  const medplum = useMedplum();
  const practitioner = useMedplumProfile() as Practitioner;
  const enqueueAmendPersist = useEnqueueLaborAmendPersist();
  const formId = "edit-milestone-form";

  const handleClose = () => {
    setOpened(false);
    if (onClose) {
      onClose();
    }
  };

  const handleNowClick = () => {
    setDateTime(new Date());
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isLoading) {
      return;
    }

    if (!practitioner || !encounterId || !clientId) {
      handleClose();
      return;
    }

    setIsLoading(true);
    try {
      const persistMilestone = async () => {
        await medplum.updateResource({
          ...observation,
          effectiveDateTime: dateTime.toISOString(),
        });

        notifications.show({
          title: "Success",
          message: "Milestone time updated successfully.",
          color: "green",
        });
      };

      const replaceKey = observation.id ? getLaborMilestoneReplaceKey(observation.id) : undefined;
      const wasAmendPersistEnqueued = enqueueAmendPersist({
        replaceKey,
        observationPreviewPatch: observation.id
          ? {
              observationId: observation.id,
              patch: { effectiveDateTime: dateTime.toISOString() },
            }
          : undefined,
        persist: persistMilestone,
        queuedMessage: LABOR_AMEND_QUEUED_MESSAGE,
        errorTitle: LABOR_AMEND_FLOWSHEET_SAVE_ERROR_TITLE,
        errorMessage: LABOR_AMEND_FLOWSHEET_SAVE_ERROR_MILESTONE_MESSAGE,
        onQueued: handleClose,
      });
      if (wasAmendPersistEnqueued) {
        return;
      }

      await persistMilestone();
      handleClose();
    } catch (error) {
      console.error("Error updating milestone:", error);
      Sentry.captureException(error);
      notifications.show({
        title: "Error",
        message: "Failed to update milestone time. Please try again.",
        color: "red",
      });
      handleClose();
    } finally {
      setIsLoading(false);
    }
  };

  const laborStage = getCodeableConceptDisplay(observation.valueCodeableConcept);

  return (
    <Drawer
      size={500}
      opened={opened}
      position="right"
      onClose={handleClose}
      closeOnEscape={false}
      withCloseButton={false}
      closeOnClickOutside={false}
      styles={{
        body: { padding: 0 },
        content: { background: "white" },
      }}
    >
      <form id={formId} onSubmit={handleSubmit}>
        <Box px="xs" bg="gray.0">
          <Group p="xs" justify="space-between">
            <Group gap="xs">
              <ActionIcon
                c="dark.9"
                variant="subtle"
                onClick={handleClose}
                data-testid="close-milestone-button"
              >
                <Tooltip label="Close" aria-label="Close">
                  <IconX size={16} />
                </Tooltip>
              </ActionIcon>
              <Title order={4} c="dark.9">
                {`Milestone: ${laborStage}`}
              </Title>
            </Group>
            <Button
              type="submit"
              form={formId}
              loading={isLoading}
              disabled={isLoading}
              data-testid="add-to-flowsheet-drawer-button"
            >
              Edit
            </Button>
          </Group>
        </Box>
        {isLoading ? (
          <Loading />
        ) : (
          <Flex p="lg" gap="lg" direction="column">
            <Group>
              <DateInput
                w={175}
                clearable
                label="Date"
                withAsterisk
                value={dateTime}
                allowDeselect={false}
                valueFormat="MM/DD/YYYY"
                placeholder="MM/DD/YYYY"
                leftSection={<IconCalendar size={20} />}
                onChange={(dateValue) => {
                  if (dateValue) {
                    const datePart = new Date(dateValue);
                    const newDateTime = new Date(dateTime);
                    datePart.setHours(newDateTime.getHours());
                    datePart.setMinutes(newDateTime.getMinutes());
                    datePart.setSeconds(newDateTime.getSeconds());
                    setDateTime(datePart);
                  }
                }}
              />
              <TimeInput
                withSeconds
                label="Time"
                withAsterisk
                ref={timeInputRef}
                value={format(dateTime, "HH:mm:ss")}
                onClick={() => timeInputRef.current?.showPicker()}
                leftSection={<IconClock size={20} stroke={1.5} />}
                onChange={(event) => {
                  const { value } = event.currentTarget;
                  if (value) {
                    const [hours, minutes, seconds] = value.split(":").map(Number);
                    const newDateTime = new Date(dateTime);
                    newDateTime.setHours(hours);
                    newDateTime.setMinutes(minutes);
                    newDateTime.setSeconds(seconds);
                    setDateTime(newDateTime);
                  }
                }}
              />
              <Button
                mt="lg"
                size="sm"
                variant="outline"
                color="burgundy.9"
                onClick={handleNowClick}
              >
                Now
              </Button>
            </Group>
          </Flex>
        )}
      </form>
    </Drawer>
  );
}
